import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useWedding } from '../context/WeddingContext';
import { getTranslation } from '../i18n';
import { getAuthenticatedSupabase } from '../supabaseClient';
import { Guest, GuestRelationshipLink, GuestRelationshipType, GuestRoleInGuestsList } from '../types';
import { GuestRelationshipPanel } from '../components/GuestRelationshipPanel';
import { ensureDefaultGuestRelationshipTypes } from '../lib/guests/relationships';
import {
  chunkGuestsByClusterRoot,
  clusterAccentClass,
  clusterRootForGuest,
  displayGuestListName,
  groupGuestLinksForList,
  isFamilyRelationshipLabel,
  sortGuestsByRelationshipClusters,
} from '../lib/guests/relationshipClusters';
import { Modal } from '../components/Modal';
import { EmptyState } from '../components/EmptyState';
import { SearchAndFilter } from '../components/SearchAndFilter';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { BulkActionsBar } from '../components/BulkActionsBar';
import { Toast, ToastMessage } from '../components/Toast';
import { filterVisibleInGuests, countCapacity } from '../lib/guests/capacity';
import {
  Users,
  Edit2,
  Baby,
  Utensils,
  CheckSquare,
  Square,
  Heart,
  User,
  PlusCircle,
  Link2,
  Trash2,
  FileText,
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';

type InvitationGuestResponseRow = {
  id: string;
  first_name: string;
  last_name: string;
  wants_accommodation: string | null;
  allergies: string | null;
  brings_children: boolean | null;
  children_count: number | null;
  message: string | null;
  created_at: string;
  guest_id: string | null;
};

function accommodationNoteFromResponse(w: string | null): string | null {
  if (!w) return null;
  const label = w === 'yes' ? 'Ano' : w === 'no' ? 'Ne' : w === 'unsure' ? 'Ještě nevím' : null;
  if (!label) return null;
  return `[Z pozvánky] Ubytování: ${label}`;
}

function invitationMessageNoteLine(raw: string | null | undefined): string | null {
  const t = raw?.trim();
  if (!t) return null;
  return `[Z pozvánky] Poznámka: ${t}`;
}

/** Odvozený počet dětí podle odpovědi (ano / ještě nevím → počet; ne → 0). */
function kidsCountFromInvitationRow(row: InvitationGuestResponseRow): number {
  if (row.brings_children === false) return 0;
  return Math.min(50, Math.max(0, row.children_count ?? 0));
}

function removeInvitationSyncedLines(notes: string | undefined, row: InvitationGuestResponseRow): string {
  const remove = new Set<string>();
  if (row.wants_accommodation) {
    const a = accommodationNoteFromResponse(row.wants_accommodation);
    if (a) remove.add(a);
  }
  const m = invitationMessageNoteLine(row.message);
  if (m) remove.add(m);
  return (notes ?? '')
    .split('\n')
    .filter((line) => !remove.has(line.trim()))
    .join('\n')
    .trim();
}

const INITIAL_GUEST: Partial<Guest> = {
  full_name: '',
  rsvp_status: 'pending',
  plus_one: false,
  kids_count: 0,
  side: 'mutual',
  role: 'guest',
  counts_for_capacity: true,
  visible_in_guests: true,
  category: 'guest',
  notes: '',
};

export default function Guests() {
  const { wedding, locale } = useWedding();
  const t = (key: string) => getTranslation(locale, key);
  const localeCode = locale === 'en' ? 'en' : 'cs';

  const [guests, setGuests] = useState<Guest[]>([]);
  const [relationshipTypes, setRelationshipTypes] = useState<GuestRelationshipType[]>([]);
  const [relationshipLinks, setRelationshipLinks] = useState<GuestRelationshipLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<Guest>>(INITIAL_GUEST);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  
  // Selection & Deletion states
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [invitationResponses, setInvitationResponses] = useState<InvitationGuestResponseRow[]>([]);
  const [invDeleteId, setInvDeleteId] = useState<string | null>(null);
  const [pairRow, setPairRow] = useState<InvitationGuestResponseRow | null>(null);
  const [pairGuestId, setPairGuestId] = useState('');
  const [pairUpdateAllergies, setPairUpdateAllergies] = useState(true);
  const [pairMergeAllergies, setPairMergeAllergies] = useState(false);
  const [pairUpdateKids, setPairUpdateKids] = useState(true);
  const [pairAddAccommodationNotes, setPairAddAccommodationNotes] = useState(true);
  const [pairAddMessageNotes, setPairAddMessageNotes] = useState(true);
  const [pairSubmitting, setPairSubmitting] = useState(false);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ id: String(Date.now()), type, text });
    setTimeout(() => setToast(null), 5000);
  };

  const fetchGuests = useCallback(async () => {
    if (!wedding) return;
    const supabase = getAuthenticatedSupabase();
    await ensureDefaultGuestRelationshipTypes(supabase, wedding.id, localeCode);
    const [guestsRes, typesRes, linksRes, invRes] = await Promise.all([
      supabase.from('guests').select('*').eq('wedding_id', wedding.id).order('full_name'),
      supabase.from('guest_relationship_types').select('*').eq('wedding_id', wedding.id).order('sort_order'),
      supabase.from('guest_relationship_links').select('*').eq('wedding_id', wedding.id),
      supabase
        .from('invitation_guest_responses')
        .select(
          'id, first_name, last_name, wants_accommodation, allergies, brings_children, children_count, message, created_at, guest_id',
        )
        .eq('wedding_id', wedding.id)
        .order('created_at', { ascending: false }),
    ]);
    if (guestsRes.data) setGuests(guestsRes.data);
    if (!invRes.error && invRes.data) setInvitationResponses(invRes.data as InvitationGuestResponseRow[]);
    else setInvitationResponses([]);
    setRelationshipTypes(typesRes.error ? [] : typesRes.data ?? []);
    setRelationshipLinks(linksRes.error ? [] : linksRes.data ?? []);
    setLoading(false);
  }, [wedding, localeCode]);

  const typeLabelById = useMemo(() => {
    const m = new Map<string, string>();
    relationshipTypes.forEach((rt) => m.set(rt.id, rt.label));
    return m;
  }, [relationshipTypes]);

  const guestById = useMemo(() => new Map(guests.map((g) => [g.id, g])), [guests]);

  /** ID typů vazby z číselníku, které se berou jako „rodina“ (řazení: rodina → ostatní skupiny → jednotlivci). */
  const familyRelationshipTypeIds = useMemo(() => {
    const s = new Set<string>();
    for (const rt of relationshipTypes) {
      if (isFamilyRelationshipLabel(rt.label)) s.add(rt.id);
    }
    return s;
  }, [relationshipTypes]);

  const visibleGuests = filterVisibleInGuests(guests);
  const capacityCount = countCapacity(guests);

  useEffect(() => {
    void fetchGuests();
  }, [fetchGuests]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!wedding) return;
    const supabase = getAuthenticatedSupabase();
    if (formData.id) {
      const { error } = await supabase.from('guests').update(formData).eq('id', formData.id);
      if (error) {
        showToast('error', `${t('guests.save_error')}: ${error.message}`);
        return;
      }
      showToast('success', t('guests.saved'));
    } else {
      const { error } = await supabase.from('guests').insert({ ...formData, wedding_id: wedding.id });
      if (error) {
        showToast('error', `${t('guests.save_error')}: ${error.message}`);
        return;
      }
      showToast('success', t('guests.saved'));
    }
    setIsModalOpen(false);
    fetchGuests();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const supabase = getAuthenticatedSupabase();
    const { error } = await supabase.from('guests').delete().eq('id', deleteId);
    setDeleteId(null);
    if (error) showToast('error', `Chyba: ${error.message}`);
    else fetchGuests();
  };

  const handleDeleteInvitationRow = async () => {
    if (!invDeleteId || !wedding) return;
    const targetId = invDeleteId;
    const supabase = getAuthenticatedSupabase();
    const { data: row, error: selErr } = await supabase
      .from('invitation_guest_responses')
      .select(
        'id, guest_id, wants_accommodation, allergies, brings_children, children_count, message',
      )
      .eq('id', targetId)
      .eq('wedding_id', wedding.id)
      .single();
    setInvDeleteId(null);
    if (selErr || !row) {
      showToast('error', selErr ? `Chyba: ${selErr.message}` : 'Odpověď nenalezena.');
      return;
    }
    const invRow = row as InvitationGuestResponseRow;

    if (invRow.guest_id) {
      const { data: guest } = await supabase
        .from('guests')
        .select('*')
        .eq('id', invRow.guest_id)
        .eq('wedding_id', wedding.id)
        .single();
      if (guest) {
        const payload: Partial<Guest> = {};
        const newNotes = removeInvitationSyncedLines(guest.notes ?? undefined, invRow);
        if (newNotes !== (guest.notes ?? '').trim()) {
          payload.notes = newNotes || null;
        }
        const inc = invRow.allergies?.trim();
        if (inc) {
          const ga = guest.allergies?.trim() ?? '';
          if (ga === inc) {
            payload.allergies = '';
          } else if (ga.endsWith(`; ${inc}`)) {
            const stripped = ga.slice(0, ga.length - inc.length - 2).trim();
            payload.allergies = stripped || null;
          }
        }
        const expectedKids = kidsCountFromInvitationRow(invRow);
        if (guest.kids_count === expectedKids) {
          payload.kids_count = 0;
        }
        if (Object.keys(payload).length > 0) {
          const { error: uErr } = await supabase
            .from('guests')
            .update(payload)
            .eq('id', guest.id)
            .eq('wedding_id', wedding.id);
          if (uErr) {
            showToast('error', `Chyba při úpravě hosta: ${uErr.message}`);
            return;
          }
        }
      }
    }

    const { error } = await supabase
      .from('invitation_guest_responses')
      .delete()
      .eq('id', targetId)
      .eq('wedding_id', wedding.id);
    if (error) {
      showToast('error', `Chyba: ${error.message}`);
      return;
    }
    showToast('success', 'Odpověď z pozvánky byla smazána.');
    void fetchGuests();
  };

  const guestsSortedForPair = useMemo(
    () => [...guests].sort((a, b) => a.full_name.localeCompare(b.full_name, 'cs')),
    [guests],
  );

  const selectedGuestForPair = useMemo(
    () => (pairGuestId ? guests.find((g) => g.id === pairGuestId) ?? null : null),
    [guests, pairGuestId],
  );

  const openPairModal = (row: InvitationGuestResponseRow) => {
    setPairRow(row);
    setPairGuestId(row.guest_id || '');
    setPairUpdateAllergies(true);
    setPairMergeAllergies(false);
    setPairUpdateKids(true);
    setPairAddAccommodationNotes(true);
    setPairAddMessageNotes(true);
  };

  const handlePairConfirm = async () => {
    if (!wedding || !pairRow || !pairGuestId) {
      showToast('error', 'Vyberte hosta ze seznamu.');
      return;
    }
    const guest = guests.find((g) => g.id === pairGuestId);
    if (!guest) return;

    setPairSubmitting(true);
    try {
      const supabase = getAuthenticatedSupabase();
      const payload: Partial<Guest> = {};

      if (pairUpdateAllergies && pairRow.allergies?.trim()) {
        const incoming = pairRow.allergies.trim();
        if (pairMergeAllergies && guest.allergies?.trim()) {
          payload.allergies = `${guest.allergies.trim()}; ${incoming}`;
        } else {
          payload.allergies = incoming;
        }
      }

      if (pairUpdateKids) {
        if (pairRow.brings_children === null || pairRow.brings_children === true) {
          payload.kids_count = Math.min(50, Math.max(0, pairRow.children_count ?? 0));
        } else if (pairRow.brings_children === false) {
          payload.kids_count = 0;
        }
      }

      let mergedNotes = guest.notes?.trim() || '';
      if (pairAddAccommodationNotes && pairRow.wants_accommodation) {
        const line = accommodationNoteFromResponse(pairRow.wants_accommodation);
        if (line) mergedNotes = mergedNotes ? `${mergedNotes}\n${line}` : line;
      }
      if (pairAddMessageNotes && pairRow.message?.trim()) {
        const line = invitationMessageNoteLine(pairRow.message);
        if (line) mergedNotes = mergedNotes ? `${mergedNotes}\n${line}` : line;
      }
      if (mergedNotes !== (guest.notes?.trim() || '')) {
        payload.notes = mergedNotes || null;
      }

      if (Object.keys(payload).length > 0) {
        const { error: uErr } = await supabase
          .from('guests')
          .update(payload)
          .eq('id', guest.id)
          .eq('wedding_id', wedding.id);
        if (uErr) throw new Error(uErr.message);
      }

      const { error: rErr } = await supabase
        .from('invitation_guest_responses')
        .update({ guest_id: guest.id })
        .eq('id', pairRow.id)
        .eq('wedding_id', wedding.id);
      if (rErr) throw new Error(rErr.message);

      showToast('success', 'Host propojen, údaje uloženy.');
      setPairRow(null);
      setPairGuestId('');
      void fetchGuests();
    } catch (e) {
      showToast('error', e instanceof Error ? e.message : 'Propojení se nepodařilo.');
    } finally {
      setPairSubmitting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Opravdu chcete smazat ${selectedIds.size} označených hostů?`)) return;
    const supabase = getAuthenticatedSupabase();
    const { error } = await supabase.from('guests').delete().in('id', Array.from(selectedIds));
    setSelectedIds(new Set());
    if (error) showToast('error', `Chyba: ${error.message}`);
    else fetchGuests();
  };

  const toggleSelection = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedIds(newSet);
  };

  const toggleSelectAllGroup = (groupGuests: Guest[]) => {
    const newSet = new Set(selectedIds);
    const allSelected = groupGuests.every(g => newSet.has(g.id));
    
    if (allSelected) {
      groupGuests.forEach(g => newSet.delete(g.id));
    } else {
      groupGuests.forEach(g => newSet.add(g.id));
    }
    setSelectedIds(newSet);
  };

  const handleOpenAdd = () => {
    setFormData({ ...INITIAL_GUEST, wedding_id: wedding?.id });
    setIsModalOpen(true);
  };

  const handleEdit = (guest: Guest) => {
    setFormData(guest);
    setIsModalOpen(true);
  };

  // Filter Logic (search/status on visible guests only)
  const filteredGuests = visibleGuests.filter(g => {
    const matchesSearch = g.full_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || g.rsvp_status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Grouping Logic
  const brideGuests = filteredGuests.filter(g => g.side === 'bride');
  const groomGuests = filteredGuests.filter(g => g.side === 'groom');
  const mutualGuests = filteredGuests.filter(g => g.side === 'mutual');

  const brideGuestsSorted = useMemo(
    () => sortGuestsByRelationshipClusters(brideGuests, relationshipLinks, familyRelationshipTypeIds),
    [brideGuests, relationshipLinks, familyRelationshipTypeIds],
  );
  const groomGuestsSorted = useMemo(
    () => sortGuestsByRelationshipClusters(groomGuests, relationshipLinks, familyRelationshipTypeIds),
    [groomGuests, relationshipLinks, familyRelationshipTypeIds],
  );
  const mutualGuestsSorted = useMemo(
    () => sortGuestsByRelationshipClusters(mutualGuests, relationshipLinks, familyRelationshipTypeIds),
    [mutualGuests, relationshipLinks, familyRelationshipTypeIds],
  );

  const renderGuestGroup = (title: string, groupGuests: Guest[], icon: React.ReactNode, avatarBg: string) => {
    if (groupGuests.length === 0) return null;
    
    const allSelected = groupGuests.every(g => selectedIds.has(g.id));

    return (
      <div className="mb-8 last:mb-0">
        <div className="flex items-center gap-3 mb-4 pl-2">
           <button 
             onClick={() => toggleSelectAllGroup(groupGuests)}
             className={`p-1 rounded-md transition-colors ${allSelected ? 'text-brand-600 bg-brand-50' : 'text-slate-300 hover:text-slate-500'}`}
           >
             {allSelected ? <CheckSquare size={18} /> : <Square size={18} />}
           </button>
           <h3 className="text-ds-meta text-slate-500 flex items-center gap-2">
             {icon}
             {title}{' '}
             <span className="text-slate-300" title={t('guests.stats.group_count_tooltip')}>
               ({countCapacity(groupGuests)})
             </span>
           </h3>
        </div>

        <div className="space-y-3">
          {chunkGuestsByClusterRoot(groupGuests, relationshipLinks).map((clusterGuests) => {
            const clusterRoot = clusterRootForGuest(groupGuests, relationshipLinks, clusterGuests[0]!.id);
            return (
              <div
                key={clusterRoot}
                className="bg-white dark:bg-slate-900 rounded-2xl border border-ds-border overflow-hidden shadow-sm"
              >
                {clusterGuests.map((guest, index) => {
                  const isSelected = selectedIds.has(guest.id);
                  const displayName = displayGuestListName(guest.full_name);
                  const initial = displayName.trim().charAt(0) || '?';
                  const linkGroups = groupGuestLinksForList(guest.id, relationshipLinks, guestById, typeLabelById);

                  return (
                    <div
                      key={guest.id}
                      onClick={() => toggleSelection(guest.id)}
                      className={`
                  group flex flex-col sm:flex-row sm:items-center justify-between cursor-pointer transition-colors
                  ${index !== clusterGuests.length - 1 ? 'border-b border-slate-100 dark:border-slate-800/80' : ''}
                  ${isSelected ? 'bg-brand-50/50 dark:bg-brand-900/10' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}
                `}
                    >
                      <div
                        className={`flex flex-1 min-w-0 items-center gap-4 mb-2 sm:mb-0 py-2 pl-2 pr-2 sm:pr-4 ${clusterAccentClass(clusterRoot)}`}
                      >
                  {/* Checkbox */}
                  <div className={`shrink-0 transition-colors ${isSelected ? 'text-brand-600' : 'text-slate-300 group-hover:text-slate-400'}`}>
                     {isSelected ? <CheckSquare size={20} /> : <Square size={20} />}
                  </div>

                  {/* Avatar */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm shrink-0 ${avatarBg}`}>
                    {initial}
                  </div>

                  {/* Info */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className={`text-sm font-normal ${isSelected ? 'text-slate-900 dark:text-slate-100' : 'text-slate-800 dark:text-slate-200'}`}>
                        {displayName}
                      </h4>
                      {guest.role !== 'guest' && (
                        <span className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-ds-meta text-slate-400">
                          {t(`guests.roles.${guest.role}`)}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col gap-0.5 mt-0.5">
                      {linkGroups.length > 0 && (
                        <div className="sr-only">{t('guests.relationships.list_badge')}</div>
                      )}
                      {linkGroups.map((lg) => (
                        <div
                          key={lg.typeId}
                          className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0 text-[11px] leading-snug text-slate-400 dark:text-slate-500"
                          title={`${lg.typeLabel}: ${lg.otherNames.join(', ')}`}
                        >
                          <Link2 size={11} className="shrink-0 opacity-45 mt-px" aria-hidden />
                          <span className="text-slate-400/95 dark:text-slate-500">{lg.typeLabel}</span>
                          <span className="text-slate-300 dark:text-slate-600 select-none" aria-hidden>
                            ·
                          </span>
                          <span className="text-slate-500 dark:text-slate-400">{lg.otherNames.join(', ')}</span>
                        </div>
                      ))}
                      <div className="flex items-center gap-3 text-xs text-slate-400">
                        {(guest.plus_one || guest.kids_count > 0) && (
                          <div className="flex items-center gap-2">
                            {guest.plus_one && <span className="flex items-center gap-1" title="Plus one"><Users size={12} /> +1</span>}
                            {guest.kids_count > 0 && <span className="flex items-center gap-1" title="Děti"><Baby size={12} /> {guest.kids_count}</span>}
                          </div>
                        )}
                        {guest.allergies && (
                          <div className="flex items-center gap-1 text-red-500" title={guest.allergies}>
                            <Utensils size={12} /> {guest.allergies}
                          </div>
                        )}
                        {guest.notes?.trim() && (
                          <div
                            className="flex items-start gap-1 text-slate-500 dark:text-slate-400 max-w-full min-w-0"
                            title={guest.notes ?? undefined}
                          >
                            <FileText size={12} className="shrink-0 mt-0.5 opacity-70" aria-hidden />
                            <span className="line-clamp-2 break-words">{guest.notes}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-4 ml-14 sm:ml-0 py-2 pr-4 pl-2 shrink-0">
                  <div className={`px-2.5 py-1 rounded-full text-ds-meta ${
                    guest.rsvp_status === 'confirmed' ? 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400' :
                    guest.rsvp_status === 'declined' ? 'bg-red-50 text-red-500 dark:bg-red-900/20 dark:text-red-400' :
                    'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400'
                  }`}>
                    {t(`guests.${guest.rsvp_status}`)}
                  </div>

                  <div className="flex items-center gap-1">
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleEdit(guest); }} 
                      className="p-2 text-slate-400 hover:text-brand-600 hover:bg-white dark:hover:bg-slate-700 rounded-xl transition-colors"
                    >
                      <Edit2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (!wedding) return null;

  return (
    <div className="space-y-8 animate-fade-in pb-24">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <h1 className="text-ds-h1 text-slate-900 dark:text-white">{t('guests.title')}</h1>
          <p className="text-ds-body text-slate-400 dark:text-slate-500 mt-2">Spravujte seznam hostů, RSVP a diety.</p>
          <div className="flex flex-wrap gap-4 mt-3 text-ds-meta">
            <span className="text-slate-400">
              {t('guests.stats.total')} (viditelní): <span className="text-slate-500 dark:text-slate-400">{visibleGuests.length}</span>
            </span>
            <span className="text-slate-400">
              Celkem osob (pro kapacity): <span className="text-slate-500 dark:text-slate-400">{capacityCount}</span>
            </span>
          </div>
        </div>
        <Button onClick={handleOpenAdd} variant="secondary" icon={<PlusCircle size={18} />}>
          {t('common.add')}
        </Button>
      </div>

      <SearchAndFilter 
        searchQuery={searchQuery} 
        onSearchChange={setSearchQuery}
      >
        <select 
          value={filterStatus} 
          onChange={(e) => setFilterStatus(e.target.value)}
          className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="all">Všechny stavy</option>
          <option value="confirmed">{t('guests.confirmed')}</option>
          <option value="pending">{t('guests.pending')}</option>
          <option value="declined">{t('guests.declined')}</option>
        </select>
      </SearchAndFilter>

      {invitationResponses.length > 0 && (
        <Card className="p-6">
          <h2 className="text-ds-h3 text-slate-900 dark:text-white mb-1">Odpovědi z veřejné pozvánky</h2>
          <p className="text-ds-meta text-slate-500 dark:text-slate-400 mb-4">
            Údaje vyplněné hosty na společné pozvánce (jméno, ubytování, alergie, děti, poznámka).
          </p>
          <div className="overflow-x-auto rounded-xl border border-ds-border">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 dark:bg-slate-800/80 text-ds-meta text-slate-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Jméno</th>
                  <th className="px-3 py-2 font-medium">Ubytování</th>
                  <th className="px-3 py-2 font-medium">Alergie</th>
                  <th className="px-3 py-2 font-medium">Děti</th>
                  <th className="px-3 py-2 font-medium max-w-[120px]">Pozn.</th>
                  <th className="px-3 py-2 font-medium">Propojeno</th>
                  <th className="px-3 py-2 font-medium whitespace-nowrap">Odesláno</th>
                  <th className="px-3 py-2 font-medium text-right w-[1%]">Akce</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {invitationResponses.map((row) => {
                  const linkedGuest = row.guest_id ? guestById.get(row.guest_id) : null;
                  return (
                    <tr key={row.id} className="text-slate-700 dark:text-slate-200">
                      <td className="px-3 py-2 font-medium">
                        {row.first_name} {row.last_name}
                      </td>
                      <td className="px-3 py-2 text-slate-600 dark:text-slate-300">
                        {row.wants_accommodation === 'yes'
                          ? 'Ano'
                          : row.wants_accommodation === 'no'
                            ? 'Ne'
                            : row.wants_accommodation === 'unsure'
                              ? 'Ještě nevím'
                              : '—'}
                      </td>
                      <td className="px-3 py-2 max-w-[200px] truncate" title={row.allergies || undefined}>
                        {row.allergies?.trim() ? row.allergies : '—'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {row.brings_children === false
                          ? 'Ne'
                          : row.brings_children === true
                            ? `Ano (${row.children_count ?? 0})`
                            : row.brings_children === null &&
                                row.children_count != null &&
                                row.children_count > 0
                              ? `Ještě nevím (${row.children_count})`
                              : '—'}
                      </td>
                      <td className="px-3 py-2 max-w-[140px] truncate text-slate-600 dark:text-slate-300 text-xs" title={row.message || undefined}>
                        {row.message?.trim() ? row.message : '—'}
                      </td>
                      <td className="px-3 py-2 text-slate-600 dark:text-slate-300">
                        {linkedGuest ? (
                          <span className="inline-flex items-center gap-1">
                            <Link2 size={14} className="text-brand-600 shrink-0" aria-hidden />
                            {linkedGuest.full_name}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap text-xs">
                        {new Date(row.created_at).toLocaleString(localeCode === 'en' ? 'en-GB' : 'cs-CZ')}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => openPairModal(row)}
                          className="inline-flex items-center justify-center p-2 rounded-lg text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-950/40"
                          title="Propojit s hostem"
                        >
                          <Link2 size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setInvDeleteId(row.id)}
                          className="inline-flex items-center justify-center p-2 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 ml-1"
                          title="Smazat"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {filteredGuests.length === 0 ? (
        <EmptyState 
          icon={Users} 
          title="Žádní hosté" 
          description="Zatím jste nepřidali žádné hosty odpovídající filtrům." 
          action={{ label: t('common.add'), onClick: handleOpenAdd }}
        />
      ) : (
        <div className="space-y-2">
          {renderGuestGroup(t('guests.stats.bride_side'), brideGuestsSorted, <Heart size={16} className="text-rose-300" strokeWidth={1.5} />, 'bg-rose-100 text-rose-600')}
          {renderGuestGroup(t('guests.stats.groom_side'), groomGuestsSorted, <User size={16} className="text-blue-300" strokeWidth={1.5} />, 'bg-blue-100 text-blue-600')}
          {renderGuestGroup(t('guests.stats.mutual'), mutualGuestsSorted, <Users size={16} className="text-slate-300" strokeWidth={1.5} />, 'bg-slate-100 text-slate-600')}
        </div>
      )}

      {/* Bulk Actions Bar - shows when items are selected */}
      <BulkActionsBar 
        selectedCount={selectedIds.size} 
        onClear={() => setSelectedIds(new Set())}
        onDelete={handleBulkDelete}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={formData.id ? t('guests.edit') : t('guests.add')}
        maxWidth="max-w-2xl"
      >
        <form onSubmit={handleSave} className="space-y-5 max-h-[min(85vh,720px)] overflow-y-auto pr-1">
          <div>
            <label className="text-ds-meta text-slate-500 mb-2 block">{t('guests.name')}</label>
            <input 
              required
              type="text" 
              value={formData.full_name || ''} 
              onChange={e => setFormData({ ...formData, full_name: e.target.value })}
              className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
             <div>
                <label className="text-ds-meta text-slate-500 mb-2 block">{t('guests.role')}</label>
                <select 
                  value={formData.role && !['bride', 'groom'].includes(formData.role) ? formData.role : 'guest'} 
                  onChange={e => setFormData({...formData, role: e.target.value as GuestRoleInGuestsList})}
                  className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200"
                >
                  {(['guest', 'witness', 'bridesmaid', 'best_man', 'parent', 'wedding_coordinator'] as const).map(r => (
                    <option key={r} value={r}>{t(`guests.roles.${r}`)}</option>
                  ))}
                </select>
             </div>
             <div>
                <label className="text-ds-meta text-slate-500 mb-2 block">{t('guests.form.side')}</label>
                <select 
                  value={formData.side} 
                  onChange={e => setFormData({...formData, side: e.target.value as any})}
                  className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200"
                >
                  <option value="bride">{t('guests.form.bride')}</option>
                  <option value="groom">{t('guests.form.groom')}</option>
                  <option value="mutual">{t('guests.form.mutual')}</option>
                </select>
             </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
               <label className="text-ds-meta text-slate-500 mb-2 block">{t('guests.status')}</label>
               <select 
                  value={formData.rsvp_status} 
                  onChange={e => setFormData({...formData, rsvp_status: e.target.value as any})}
                  className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200"
               >
                  <option value="pending">{t('guests.pending')}</option>
                  <option value="confirmed">{t('guests.confirmed')}</option>
                  <option value="declined">{t('guests.declined')}</option>
                </select>
            </div>
            <div>
               <label className="text-ds-meta text-slate-500 mb-2 block">{t('guests.kids')}</label>
               <input 
                  type="number" 
                  min="0"
                  value={formData.kids_count} 
                  onChange={e => setFormData({...formData, kids_count: parseInt(e.target.value) || 0})}
                  className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200"
                />
            </div>
          </div>

          <div className="flex items-center gap-3">
              <input 
                type="checkbox" 
                id="plus_one"
                checked={formData.plus_one} 
                onChange={e => setFormData({...formData, plus_one: e.target.checked})}
                className="w-5 h-5 rounded text-brand-600 focus:ring-brand-500 border-gray-300"
              />
              <label htmlFor="plus_one" className="text-ds-body text-slate-600 dark:text-slate-300">{t('guests.form.plus_one')}</label>
          </div>

          <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
             <label className="text-ds-meta text-slate-500 mb-2 block flex items-center gap-2">
                <Utensils size={12} /> {t('guests.form.allergies')}
             </label>
             <input 
              type="text" 
              value={formData.allergies || ''} 
              onChange={e => setFormData({ ...formData, allergies: e.target.value })}
              className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200"
              placeholder="Např. Lepek, laktóza, vegetarián..."
            />
          </div>

          <div>
            <label
              htmlFor="guest-notes"
              className="text-ds-meta text-slate-500 mb-2 block flex items-center gap-2"
            >
              <FileText size={12} /> {t('guests.form.notes')}
            </label>
            <textarea
              id="guest-notes"
              rows={4}
              value={formData.notes ?? ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200 resize-y min-h-[88px]"
              placeholder={
                localeCode === 'en'
                  ? 'Internal notes, incl. lines from the public invitation (e.g. accommodation).'
                  : 'Vnitřní poznámky k hostovi, včetně řádků z veřejné pozvánky (např. ubytování).'
              }
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
             <div>
                <label className="text-ds-meta text-slate-500 mb-2 block">{t('guests.form.email')}</label>
                <input 
                  type="email" 
                  value={formData.contact_email || ''} 
                  onChange={e => setFormData({ ...formData, contact_email: e.target.value })}
                  className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200"
                />
             </div>
             <div>
                <label className="text-ds-meta text-slate-500 mb-2 block">{t('guests.form.phone')}</label>
                <input 
                  type="tel" 
                  value={formData.contact_phone || ''} 
                  onChange={e => setFormData({ ...formData, contact_phone: e.target.value })}
                  className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-slate-300 text-slate-700 dark:text-slate-200"
                />
             </div>
          </div>

          {wedding && (
            <GuestRelationshipPanel
              weddingId={wedding.id}
              guestId={formData.id}
              guests={guests}
              relationshipTypes={relationshipTypes}
              links={relationshipLinks}
              typeLabelById={typeLabelById}
              onRefresh={fetchGuests}
              t={t}
              onToast={showToast}
            />
          )}

          <Button type="submit" className="w-full">
            {t('common.save')}
          </Button>
        </form>
      </Modal>

      <Modal
        isOpen={!!pairRow}
        onClose={() => {
          if (pairSubmitting) return;
          setPairRow(null);
          setPairGuestId('');
        }}
        title="Propojit s hostem"
        maxWidth="max-w-lg"
      >
        {pairRow && (
          <div className="space-y-5">
            <p className="text-ds-body text-slate-600 dark:text-slate-300">
              Odpověď z pozvánky:{' '}
              <span className="font-semibold text-slate-800 dark:text-slate-100">
                {pairRow.first_name} {pairRow.last_name}
              </span>
            </p>

            <div>
              <label htmlFor="pair-guest-select" className="text-ds-meta text-slate-500 mb-2 block">
                Host ve vašem seznamu
              </label>
              <select
                id="pair-guest-select"
                value={pairGuestId}
                onChange={(e) => setPairGuestId(e.target.value)}
                className="w-full p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 outline-none focus:border-brand-500 text-slate-700 dark:text-slate-200"
              >
                <option value="">— Vyberte hosta —</option>
                {guestsSortedForPair.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.full_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-3 rounded-xl border border-slate-100 dark:border-slate-700 p-4 bg-slate-50/80 dark:bg-slate-900/40">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 w-5 h-5 rounded text-brand-600 focus:ring-brand-500 border-gray-300"
                  checked={pairUpdateAllergies}
                  onChange={(e) => {
                    setPairUpdateAllergies(e.target.checked);
                    if (!e.target.checked) setPairMergeAllergies(false);
                  }}
                />
                <span className="text-ds-body text-slate-700 dark:text-slate-200">
                  Přepsat alergie hosta podle odpovědi z pozvánky
                </span>
              </label>
              {pairUpdateAllergies &&
                selectedGuestForPair?.allergies?.trim() &&
                pairRow.allergies?.trim() && (
                  <label className="flex items-start gap-3 cursor-pointer pl-8">
                    <input
                      type="checkbox"
                      className="mt-1 w-5 h-5 rounded text-brand-600 focus:ring-brand-500 border-gray-300"
                      checked={pairMergeAllergies}
                      onChange={(e) => setPairMergeAllergies(e.target.checked)}
                    />
                    <span className="text-ds-body text-slate-700 dark:text-slate-200">
                      Sloučit s existujícími alergiemi hosta (oddělené středníkem)
                    </span>
                  </label>
                )}

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 w-5 h-5 rounded text-brand-600 focus:ring-brand-500 border-gray-300"
                  checked={pairUpdateKids}
                  onChange={(e) => setPairUpdateKids(e.target.checked)}
                />
                <span className="text-ds-body text-slate-700 dark:text-slate-200">
                  Aktualizovat počet dětí u hosta (včetně „ještě nevím“)
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 w-5 h-5 rounded text-brand-600 focus:ring-brand-500 border-gray-300"
                  checked={pairAddAccommodationNotes}
                  onChange={(e) => setPairAddAccommodationNotes(e.target.checked)}
                  disabled={!pairRow.wants_accommodation}
                />
                <span
                  className={
                    !pairRow.wants_accommodation
                      ? 'text-ds-body text-slate-400'
                      : 'text-ds-body text-slate-700 dark:text-slate-200'
                  }
                >
                  Přidat řádek o ubytování do poznámek hosta
                  {!pairRow.wants_accommodation && ' (v odpovědi neuvedeno)'}
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 w-5 h-5 rounded text-brand-600 focus:ring-brand-500 border-gray-300"
                  checked={pairAddMessageNotes}
                  onChange={(e) => setPairAddMessageNotes(e.target.checked)}
                  disabled={!pairRow.message?.trim()}
                />
                <span
                  className={
                    !pairRow.message?.trim()
                      ? 'text-ds-body text-slate-400'
                      : 'text-ds-body text-slate-700 dark:text-slate-200'
                  }
                >
                  Přidat poznámku z formuláře do poznámek hosta
                  {!pairRow.message?.trim() && ' (v odpovědi prázdné)'}
                </span>
              </label>
            </div>

            <div className="flex flex-wrap gap-3 justify-end pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  if (pairSubmitting) return;
                  setPairRow(null);
                  setPairGuestId('');
                }}
                disabled={pairSubmitting}
              >
                Zrušit
              </Button>
              <Button type="button" onClick={handlePairConfirm} isLoading={pairSubmitting} disabled={pairSubmitting}>
                Propojit a uložit
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog 
        isOpen={!!deleteId} 
        onCancel={() => setDeleteId(null)} 
        onConfirm={handleDelete}
        title={t('common.delete')}
        description={t('common.confirm_delete')}
      />

      <ConfirmDialog
        isOpen={!!invDeleteId}
        onCancel={() => setInvDeleteId(null)}
        onConfirm={handleDeleteInvitationRow}
        title="Smazat odpověď z pozvánky?"
        description="Tato akce je nevratná. Záznam zmizí ze seznamu odpovědí."
        confirmLabel="Smazat"
      />

      {toast && <Toast toast={toast} onClose={() => setToast(null)} />}
    </div>
  );
}